import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  useTheme,
  Tabs,
  Tab,
  Button,
  Avatar,
  Skeleton,
} from "@mui/material";
import { getCustomer } from "../../services/CustomerService";
import { getSalesByCustomerId } from "../../services/SaleService";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import PaymentsDialog from "../../components/Sales/PaymentsDialog";

// Profile Icons
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import LocationCityOutlinedIcon from "@mui/icons-material/LocationCityOutlined";

const CustomerProfile = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { customerId } = useParams();
  const navigate = useNavigate();

  const [customer, setCustomer] = useState(null);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const [selectedSalesId, setSelectedSalesId] = useState(null);

  useEffect(() => {
    getCustomer(customerId)
      .then((response) => setCustomer(response.data))
      .catch((error) =>
        console.error("Error fetching customer details:", error),
      );
  }, [customerId]);

  useEffect(() => {
    const fetchSales = async () => {
      try {
        const response = await getSalesByCustomerId(customerId, 0, 50); // Fetching more to show the list nicely
        // Sort newest first
        const sortedSales = [...response.data].sort((a, b) => b.id - a.id);
        setSales(sortedSales);
      } catch (error) {
        console.error("Error fetching sales data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSales();
  }, [customerId]);

  const formatDateSafe = (d) => {
    if (!d) return "-";
    try {
      return new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return String(d);
    }
  };

  const handleViewPayments = (saleId) => {
    setSelectedSalesId(saleId);
    setPaymentsOpen(true);
  };

  const handleClosePayments = () => {
    setPaymentsOpen(false);
  };

  return (
    <Box
      m={{ xs: "10px", sm: "15px", md: "20px" }}
      sx={{
        opacity: 0,
        animation: "slideInUp 0.5s ease-out forwards",
        "@keyframes slideInUp": {
          "0%": { opacity: 0, transform: "translateY(20px)" },
          "100%": { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      <Box mb="20px">
        <Header
          title="CUSTOMER PROFILE"
          subtitle="View full customer details here"
        />
      </Box>

      {}
      <Box
        sx={{
          borderBottom: 1,
          borderColor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.1)"
              : "rgba(0,0,0,0.1)",
          mb: "20px",
        }}
      >
        <Tabs
          value={activeTab}
          variant="fullWidth"
          onChange={(event, newValue) => setActiveTab(newValue)}
          aria-label="customer profile tabs"
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: "700",
              fontSize: "0.95rem",
              color: colors.gray[400],
              transition: "color 0.2s ease",
              "&.Mui-selected": {
                color: colors.blueAccent[500],
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: colors.blueAccent[500],
              height: 3,
              borderTopLeftRadius: 3,
              borderTopRightRadius: 3,
            },
          }}
        >
          <Tab label="Basic Profile" />
          <Tab label="Purchase History" />
        </Tabs>
      </Box>

      {}
      {activeTab === 0 && (
        <Box
          sx={{
            backgroundColor:
              theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
            borderRadius: "12px",
            border: `1px solid ${
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.05)"
                : "rgba(0,0,0,0.1)"
            }`,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 4px 20px rgba(0,0,0,0.2)"
                : "0 4px 20px rgba(0,0,0,0.03)",
            overflow: "hidden",
            margin: "30px 0",
            animation: "fadeIn 0.4s ease-out",
            "@keyframes fadeIn": {
              from: { opacity: 0, transform: "translateY(10px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          <Box p={{ xs: 3, sm: 4 }}>
            {/* Header Section */}
            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              alignItems="center"
              mb={5}
              gap={3}
              textAlign={{ xs: "center", sm: "left" }}
            >
              {loading ? (
                <Skeleton
                  variant="circular"
                  width={110}
                  height={110}
                  sx={{
                    bgcolor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.06)",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    padding: "4px",
                    borderRadius: "50%",
                    background: `linear-gradient(135deg, ${colors.blueAccent[400]} 0%, ${colors.greenAccent[400]} 100%)`,
                    boxShadow: `0px 8px 24px ${
                      theme.palette.mode === "dark"
                        ? "rgba(99, 102, 241, 0.4)"
                        : "rgba(79, 70, 229, 0.3)"
                    }`,
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? colors.primary[400]
                          : "#ffffff",
                      color: colors.gray[100],
                      width: 102,
                      height: 102,
                      fontSize: "3rem",
                      fontWeight: "bold",
                      border: `4px solid ${
                        theme.palette.mode === "dark"
                          ? colors.primary[400]
                          : "#ffffff"
                      }`,
                    }}
                  >
                    {customer?.fullName ? (
                      customer.fullName.charAt(0).toUpperCase()
                    ) : (
                      <PersonOutlinedIcon fontSize="large" />
                    )}
                  </Avatar>
                </Box>
              )}

              <Box flex="1">
                {loading ? (
                  <>
                    <Skeleton
                      variant="text"
                      width="50%"
                      height={50}
                      sx={{
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.08)"
                            : "rgba(0,0,0,0.06)",
                        mx: { xs: "auto", sm: 0 },
                      }}
                    />
                    <Skeleton
                      variant="rounded"
                      width={100}
                      height={28}
                      sx={{
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.08)"
                            : "rgba(0,0,0,0.06)",
                        mt: 1,
                        borderRadius: "20px",
                        mx: { xs: "auto", sm: 0 },
                      }}
                    />
                  </>
                ) : (
                  <>
                    <Typography
                      variant="h2"
                      fontWeight="800"
                      color={colors.gray[100]}
                      sx={{ letterSpacing: "-0.5px" }}
                    >
                      {customer?.fullName || "N/A"}
                    </Typography>
                    <Box mt={1.5}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "inline-block",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(99, 102, 241, 0.15)"
                              : "rgba(79, 70, 229, 0.1)",
                          color: colors.blueAccent[500],
                          padding: "6px 14px",
                          borderRadius: "20px",
                          fontWeight: "700",
                          letterSpacing: "1px",
                          textTransform: "uppercase",
                        }}
                      >
                        Customer
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            </Box>

            {/* Profile Details Grid */}
            <Grid container spacing={3}>
              {[
                {
                  label: "Full Name",
                  value: customer?.fullName,
                  icon: <BadgeOutlinedIcon fontSize="small" />,
                },
                {
                  label: "Email",
                  value: customer?.email,
                  icon: <EmailOutlinedIcon fontSize="small" />,
                },
                {
                  label: "Phone Number",
                  value: customer?.phoneNumber,
                  icon: <PhoneOutlinedIcon fontSize="small" />,
                },
                {
                  label: "Store Address",
                  value: customer?.storeAddress,
                  icon: <StoreOutlinedIcon fontSize="small" />,
                },
                {
                  label: "City",
                  value: customer?.city,
                  icon: <LocationCityOutlinedIcon fontSize="small" />,
                },
              ].map((item, index) => (
                <Grid item xs={12} sm={6} key={index} sx={{ display: "flex" }}>
                  {loading ? (
                    <Skeleton
                      variant="rounded"
                      width="100%"
                      height={90}
                      sx={{
                        borderRadius: "12px",
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.04)"
                            : "rgba(0,0,0,0.04)",
                      }}
                    />
                  ) : (
                    <Box
                      width="100%"
                      display="flex"
                      flexDirection="column"
                      p="20px"
                      borderRadius="12px"
                      backgroundColor={
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.02)"
                          : colors.primary[500]
                      }
                      border={`1px solid ${
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.05)"
                          : "rgba(0,0,0,0.08)"
                      }`}
                      sx={{
                        transition: "all 0.2s ease, box-shadow 0.2s ease",
                        "&:hover": {
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.04)"
                              : colors.primary[600],
                          boxShadow:
                            theme.palette.mode === "dark"
                              ? "none"
                              : "0 4px 12px rgba(0,0,0,0.04)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            color: colors.greenAccent[500],
                            display: "flex",
                          }}
                        >
                          {item.icon}
                        </Box>
                        <Typography
                          variant="caption"
                          color={colors.gray[400]}
                          textTransform="uppercase"
                          letterSpacing="1px"
                          fontWeight="700"
                        >
                          {item.label}
                        </Typography>
                      </Box>
                      <Typography
                        variant="h5"
                        color={colors.gray[100]}
                        fontWeight="600"
                        mt={1}
                        sx={{ wordBreak: "break-word" }}
                      >
                        {item.value || "N/A"}
                      </Typography>
                    </Box>
                  )}
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      )}

      {}
      {activeTab === 1 && (
        <Box
          sx={{
            margin: "30px 0",
            animation: "fadeIn 0.4s ease-out",
          }}
        >
          <Box
            sx={{
              backgroundColor:
                theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
              borderRadius: "12px",
              border: `1px solid ${
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(0,0,0,0.1)"
              }`,
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 4px 20px rgba(0,0,0,0.2)"
                  : "0 4px 20px rgba(0,0,0,0.03)",
              overflow: "hidden",
            }}
          >
            {/* List Header (Desktop Only) */}
            <Box
              display={{ xs: "none", md: "flex" }}
              p="16px 24px"
              borderBottom={`1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`}
              backgroundColor={
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.02)"
                  : "rgba(0,0,0,0.01)"
              }
            >
              <Typography
                color={colors.gray[400]}
                variant="caption"
                fontWeight="700"
                textTransform="uppercase"
                letterSpacing="1px"
                sx={{ flex: 1.5 }}
              >
                Product & Ref
              </Typography>
              <Typography
                color={colors.gray[400]}
                variant="caption"
                fontWeight="700"
                textTransform="uppercase"
                letterSpacing="1px"
                sx={{ flex: 1 }}
              >
                Date
              </Typography>
              <Typography
                color={colors.gray[400]}
                variant="caption"
                fontWeight="700"
                textTransform="uppercase"
                letterSpacing="1px"
                sx={{ flex: 1 }}
              >
                Amount
              </Typography>
              <Typography
                color={colors.gray[400]}
                variant="caption"
                fontWeight="700"
                textTransform="uppercase"
                letterSpacing="1px"
                sx={{ flex: 1 }}
              >
                Outstanding
              </Typography>
              <Typography
                color={colors.gray[400]}
                variant="caption"
                fontWeight="700"
                textTransform="uppercase"
                letterSpacing="1px"
                sx={{ flex: 1.5, textAlign: "right" }}
              >
                Status / Actions
              </Typography>
            </Box>

            {/* List Body */}
            <Box>
              {loading ? (
                [...Array(3)].map((_, i) => (
                  <Box
                    key={i}
                    display="flex"
                    p="20px 24px"
                    borderBottom={`1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`}
                  >
                    <Skeleton
                      variant="rounded"
                      width="100%"
                      height={40}
                      sx={{
                        bgcolor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.04)"
                            : "rgba(0,0,0,0.04)",
                      }}
                    />
                  </Box>
                ))
              ) : sales.length > 0 ? (
                sales.map((sale, index) => (
                  <Box
                    key={sale.id}
                    display="flex"
                    flexDirection={{ xs: "column", md: "row" }}
                    alignItems={{ xs: "flex-start", md: "center" }}
                    p="20px 24px"
                    gap={{ xs: 2, md: 0 }}
                    borderBottom={
                      index === sales.length - 1
                        ? "none"
                        : `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`
                    }
                    sx={{
                      transition: "background-color 0.2s ease",
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.02)"
                            : "rgba(0,0,0,0.02)",
                      },
                    }}
                  >
                    {/* Product & Ref */}
                    <Box sx={{ flex: 1.5, width: "100%" }}>
                      <Typography
                        color={colors.gray[100]}
                        variant="h5"
                        fontWeight="600"
                        mb="4px"
                      >
                        {sale.title}
                      </Typography>
                      <Typography
                        color={colors.gray[400]}
                        variant="caption"
                        sx={{ letterSpacing: "0.5px" }}
                      >
                        #TRX-{sale.id}
                      </Typography>
                    </Box>

                    {/* Date */}
                    <Box sx={{ flex: 1, width: "100%" }}>
                      <Typography
                        display={{ xs: "block", md: "none" }}
                        variant="caption"
                        color={colors.gray[400]}
                        mb="4px"
                      >
                        Date
                      </Typography>
                      <Typography
                        color={colors.gray[300]}
                        variant="body2"
                        fontWeight="500"
                      >
                        {formatDateSafe(sale.date)}
                      </Typography>
                    </Box>

                    {/* Amount & Quantity */}
                    <Box sx={{ flex: 1, width: "100%" }}>
                      <Typography
                        display={{ xs: "block", md: "none" }}
                        variant="caption"
                        color={colors.gray[400]}
                        mb="4px"
                      >
                        Amount
                      </Typography>
                      <Typography
                        color={colors.gray[100]}
                        variant="body1"
                        fontWeight="600"
                      >
                        ₦{Number(sale.totalBalance).toLocaleString()}
                      </Typography>
                      <Typography color={colors.gray[400]} variant="caption">
                        Qty: {sale.quantity}
                      </Typography>
                    </Box>

                    {/* Outstanding */}
                    <Box sx={{ flex: 1, width: "100%" }}>
                      <Typography
                        display={{ xs: "block", md: "none" }}
                        variant="caption"
                        color={colors.gray[400]}
                        mb="4px"
                      >
                        Outstanding
                      </Typography>
                      <Typography
                        color={
                          Number(sale.outstandingBalance) > 0
                            ? colors.redAccent[500]
                            : colors.greenAccent[500]
                        }
                        variant="body1"
                        fontWeight="600"
                      >
                        ₦{Number(sale.outstandingBalance).toLocaleString()}
                      </Typography>
                    </Box>

                    {/* Status / Actions */}
                    <Box
                      sx={{
                        flex: 1.5,
                        width: "100%",
                        display: "flex",
                        justifyContent: { xs: "flex-start", md: "flex-end" },
                      }}
                    >
                      {(() => {
                        const status = sale.paymentStatus;
                        if (status === "FULLY_PAID") {
                          return (
                            <Box
                              display="inline-flex"
                              alignItems="center"
                              px={2}
                              py={1}
                              borderRadius="20px"
                              backgroundColor={
                                theme.palette.mode === "dark"
                                  ? "rgba(76, 206, 172, 0.15)"
                                  : "rgba(16, 185, 129, 0.1)"
                              }
                              color={colors.greenAccent[500]}
                            >
                              <CheckCircleOutlinedIcon
                                sx={{ mr: "6px", fontSize: "18px" }}
                              />
                              <Typography variant="body2" fontWeight="700">
                                Completed
                              </Typography>
                            </Box>
                          );
                        }
                        if (status === "PENDING_PAYMENT") {
                          return (
                            <Box
                              display="inline-flex"
                              alignItems="center"
                              px={2}
                              py={1}
                              borderRadius="20px"
                              backgroundColor={
                                theme.palette.mode === "dark"
                                  ? "rgba(244, 63, 94, 0.15)"
                                  : "rgba(239, 68, 68, 0.1)"
                              }
                              color={colors.redAccent[500]}
                            >
                              <WarningAmberOutlinedIcon
                                sx={{ mr: "6px", fontSize: "18px" }}
                              />
                              <Typography variant="body2" fontWeight="700">
                                Not Paid
                              </Typography>
                            </Box>
                          );
                        }
                        if (status === "PARTIAL_PAYMENT") {
                          return (
                            <Button
                              variant="outlined"
                              onClick={() => handleViewPayments(sale.id)}
                              sx={{
                                borderColor:
                                  theme.palette.mode === "dark"
                                    ? colors.blueAccent[400]
                                    : colors.blueAccent[600],
                                color:
                                  theme.palette.mode === "dark"
                                    ? colors.blueAccent[400]
                                    : colors.blueAccent[600],
                                borderRadius: "20px",
                                padding: "6px 16px",
                                textTransform: "none",
                                fontWeight: "700",
                                transition: "all 0.2s",
                                "&:hover": {
                                  backgroundColor:
                                    theme.palette.mode === "dark"
                                      ? "rgba(129, 140, 248, 0.1)"
                                      : "rgba(79, 70, 229, 0.1)",
                                  borderColor:
                                    theme.palette.mode === "dark"
                                      ? colors.blueAccent[300]
                                      : colors.blueAccent[700],
                                },
                              }}
                            >
                              <VisibilityOutlinedIcon
                                sx={{ mr: "6px", fontSize: "18px" }}
                              />{" "}
                              View Plan
                            </Button>
                          );
                        }
                        return (
                          <Typography variant="body2" color={colors.gray[500]}>
                            N/A
                          </Typography>
                        );
                      })()}
                    </Box>
                  </Box>
                ))
              ) : (
                <Box
                  p="60px 20px"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.02)"
                          : "rgba(0,0,0,0.02)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                    }}
                  >
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={colors.blueAccent[500]}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="9" cy="21" r="1"></circle>
                      <circle cx="20" cy="21" r="1"></circle>
                      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                    </svg>
                  </Box>
                  <Typography
                    variant="h5"
                    color={colors.gray[100]}
                    fontWeight="600"
                    mb="4px"
                  >
                    No Purchases Yet
                  </Typography>
                  <Typography
                    variant="body2"
                    color={colors.gray[400]}
                    textAlign="center"
                  >
                    This customer hasn't made any purchases recorded in the
                    system.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}

      <PaymentsDialog
        open={paymentsOpen}
        onClose={handleClosePayments}
        salesId={selectedSalesId}
      />
    </Box>
  );
};

export default CustomerProfile;
